import {ButtonProps, openDialog, showNotification} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {equals, isNil, not, head} from 'ramda';
import {isArray, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {CorrectionType} from '@dms/api/shared';
import {
  BulkPostReceiveNoteCorrectionItemsRequest,
  PatchReceiveNoteCorrectionItemsDeleteRequest,
  ReceiveNoteCorrectionBasketItem,
  useBulkPostReceiveNoteCorrectionItemsMutation,
  useDeleteReceiveNoteCorrectionItemsMutation,
  useGetReceiveNoteCorrectionBasketQuery,
  usePatchReceiveNoteCorrectionBasketItemCorrectionValueMutation,
  usePatchReceiveNoteCorrectionBasketItemQuantityMutation,
  usePatchReceiveNoteCorrectionItemMutation,
  usePostReceiveNoteCorrectionBasketCheckoutMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  EitherQuantityOrError,
  FullScreenModal,
  WAREHOUSE_GRID_CODES,
  handleApiError,
  logError,
  refreshDmsDatagrid,
  useInvalidBasketItemsIds,
} from '@dms/shared';
import {
  AfterSalesCorrectionBasket,
  CorrectionBasketItemEditingDetails,
} from '@dms/shared-aftersales';

import {composePath, noop, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ReceiveNoteCorrectionAddMaterialList} from './ReceiveNoteCorrectionAddMaterialList';
import {ReceiveNoteCorrectionItemEdit} from './ReceiveNoteCorrectionItemEdit';

interface ReceiveNoteCorrectionAddMaterialModalProps extends RequiredTestIdProps {
  receiveNoteCorrectionId: string;
  receiveNoteId: string;
  isReceiveNoteCorrectionCompleted: boolean;
  onClose: VoidFunction;
}

export function ReceiveNoteCorrectionAddMaterialModal(
  props: ReceiveNoteCorrectionAddMaterialModalProps
) {
  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetReceiveNoteCorrectionBasketQuery({
    creditNoteId: props.receiveNoteCorrectionId,
  });

  const [checkoutBasket, {isLoading: isCheckingOutBasket}] =
    usePostReceiveNoteCorrectionBasketCheckoutMutation();

  const [deleteBasketItems, {isLoading: isDeletingBasketItems}] =
    useDeleteReceiveNoteCorrectionItemsMutation();

  const [bulkAddItemsToBasket, {isLoading: isAddingItemsToBasket}] =
    useBulkPostReceiveNoteCorrectionItemsMutation();

  const [changeBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchReceiveNoteCorrectionBasketItemQuantityMutation();

  const [changeBasketItemCorrectionValue, {isLoading: isChangingCorrectionValue}] =
    usePatchReceiveNoteCorrectionBasketItemCorrectionValueMutation();

  const [, {isLoading: isEditingCorrectionItem}] = usePatchReceiveNoteCorrectionItemMutation({
    fixedCacheKey: 'edit-receive-note-correction-item',
  });

  const basketItems = basket?.creditNoteBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.creditNoteBasketTotalPrice;

  const basketItemsWithZeroCorrectionValue = basketItems
    .filter(
      (basketItem) =>
        basketItem.correctionType === 'VALUE_CORRECTION' && basketItem.correctionValue === 0
    )
    .map((basketItem) => basketItem.id);

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isDeletingBasketItems ||
    isCheckingOutBasket ||
    isChangingQuantity ||
    isChangingCorrectionValue ||
    isEditingCorrectionItem;

  const {setInvalidBasketItemId, hasInvalidBasketItems} = useInvalidBasketItemsIds(basketItems);

  const hasInvalidItems =
    hasInvalidBasketItems || isNotNilOrEmpty(basketItemsWithZeroCorrectionValue);

  const handleCheckout = async () => {
    await checkoutBasket({
      creditNoteId: props.receiveNoteCorrectionId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.materialAdded')))
      .then(props.onClose)
      .catch(handleApiError);
  };

  const handleDiscard = async () => {
    await deleteBasketItems({
      creditNoteId: props.receiveNoteCorrectionId,
      body: {state: 'BASKET'},
    })
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);
  };

  const handleAddMaterialToBasket = async (itemsIds: string[], correctionType: CorrectionType) => {
    const requestBody: BulkPostReceiveNoteCorrectionItemsRequest['body'] = {
      receiveNoteItemId: itemsIds,
      correctionType,
    };

    await bulkAddItemsToBasket({
      creditNoteId: props.receiveNoteCorrectionId,
      body: requestBody,
    })
      .unwrap()
      .then((result) => {
        if (correctionType === 'RETURN_AND_PRICE_CHANGE') {
          const itemId = head(result.creditNoteItemId);

          if (!itemId) {
            return logError('creditNoteItemId is not defined');
          }

          openDialog(
            <ReceiveNoteCorrectionItemEdit
              receiveNoteCorrectionId={props.receiveNoteCorrectionId}
              receiveNoteCorrectionItemId={itemId}
              isReceiveNoteCorrectionCompleted={props.isReceiveNoteCorrectionCompleted}
              onAfterSubmit={noop}
              data-testid={suffixTestId('itemEditDialog', props)}
            />,
            {
              id: 'itemEditDialog',
              title: i18n.t('entity.warehouse.labels.editReceiveNoteCorrectionItem'),
              size: 'large',
              withAdditionalFooter: true,
              'data-testid': suffixTestId('itemEditDialog', props),
            }
          );
        }
      })
      .catch(handleApiError);
  };

  const handleOpenMaterialDetailInNewTab = (warehouseId: string, articleId: string) => {
    window.open(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {warehouseId, id: articleId},
        isSearchParamsPreserved: false,
      }),
      '_blank'
    );
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setInvalidBasketItemId(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const correctionType = basketItem?.correctionType;
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged) || isNil(correctionType)) {
      return;
    }

    const request =
      correctionType === 'VALUE_CORRECTION'
        ? changeBasketItemCorrectionValue({
            creditNoteId: props.receiveNoteCorrectionId,
            creditNoteItemId: itemId,
            body: {correctionValue: quantity.newQuantity},
          })
        : changeBasketItemQuantity({
            creditNoteId: props.receiveNoteCorrectionId,
            creditNoteItemId: itemId,
            body: {correctionQuantity: quantity.newQuantity},
          });

    await request.unwrap().catch(handleApiError);
  };

  const handleEdit = (editingDetails: CorrectionBasketItemEditingDetails) =>
    openDialog(
      <ReceiveNoteCorrectionItemEdit
        receiveNoteCorrectionId={props.receiveNoteCorrectionId}
        receiveNoteCorrectionItemId={editingDetails.itemId}
        isReceiveNoteCorrectionCompleted={props.isReceiveNoteCorrectionCompleted}
        onAfterSubmit={noop}
        data-testid={suffixTestId('itemEditDialog', props)}
      />,
      {
        id: 'itemEditDialog',
        title: i18n.t('entity.warehouse.labels.editReceiveNoteCorrectionItem'),
        size: 'large',
        withAdditionalFooter: true,
        'data-testid': suffixTestId('itemEditDialog', props),
      }
    );

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody: PatchReceiveNoteCorrectionItemsDeleteRequest['body'] = {
      state: 'BASKET',
      creditNoteItemId: ids,
    };

    await deleteBasketItems({
      creditNoteId: props.receiveNoteCorrectionId,
      body: requestBody,
    })
      .unwrap()
      .then(() => {
        refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNoteItemAdd, {deselectAll: true});
      })
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: hasPendingRequest,
      isDisabled: isBasketEmpty || hasInvalidItems,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal
      headline={i18n.t('general.actions.addMaterial')}
      actions={actions}
      data-testid={props['data-testid']}
    >
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <ReceiveNoteCorrectionAddMaterialList
              receiveNoteId={props.receiveNoteId}
              onAddMaterialToBasket={handleAddMaterialToBasket}
              onOpenMaterialDetailInNewTab={handleOpenMaterialDetailInNewTab}
              hasPendingRequest={hasPendingRequest}
              data-testid={suffixTestId('materialList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesCorrectionBasket<ReceiveNoteCorrectionBasketItem>
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                hasPendingRequest,
                hasInvalidItems,
              }}
              mechanic={{isMechanicHidden: true}}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
